// src/features/kommunikation/components/NachversandModal.tsx
//
// Nachversand an Mitarbeiter (02.11.2026, Freigabe Tim).
//
// Zeigt alle Monate, in denen Gehaltsunterlagen eines Mitarbeiters nur per
// Sammelpaket an den Arbeitgeber gingen, obwohl der Mitarbeiter inzwischen
// eine E-Mail-Adresse in DATEV hat. Ausgewählte Fälle werden über die Edge
// Function lohnschein-nachversand direkt an den Mitarbeiter versendet, mit
// derselben Logik wie der reguläre Lohnschein-Versand. Ausschließlich manuell.
//
// Versendet wird je Kunde und Monat in Gruppen von höchstens
// MAX_JE_AUFRUF Mitarbeitern nacheinander. So bleibt jeder Aufruf klein, und
// ein Fehler betrifft nur die eigene Gruppe. Das Ergebnis steht je Zeile.
//
// Schwester-Komponente von SonderversandModal, bewusst eigenständig gehalten,
// damit Monatsversand und Sonderversand vollständig unberührt bleiben.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { nachversandService } from '../services/NachversandService';
import { NachversandFall } from '../types';

interface NachversandModalProps {
  onClose: () => void;
  // Nach einem Versand, damit das Dashboard die neuen Dokumente zeigt.
  onSuccess: () => void;
}

// Muss zur Grenze in supabase/functions/lohnschein-nachversand passen.
const MAX_JE_AUFRUF = 5;

type Phase = 'laden' | 'bereit' | 'sendet' | 'fertig' | 'fehler';

type ZeilenErgebnis =
  | { art: 'versendet' }
  | { art: 'nicht_moeglich'; text: string }
  | { art: 'fehler'; text: string }
  | { art: 'uebersprungen'; text: string };

function datumBerlin(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

export const NachversandModal: React.FC<NachversandModalProps> = ({ onClose, onSuccess }) => {
  const [phase, setPhase] = useState<Phase>('laden');
  const [fehler, setFehler] = useState<string | null>(null);
  const [faelle, setFaelle] = useState<NachversandFall[]>([]);
  const [kundeFilter, setKundeFilter] = useState<string>('alle');
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [ergebnisse, setErgebnisse] = useState<Record<string, ZeilenErgebnis>>({});
  const [fortschritt, setFortschritt] = useState<{ erledigt: number; gesamt: number }>({ erledigt: 0, gesamt: 0 });

  const laden = useCallback(async () => {
    setPhase('laden');
    setFehler(null);
    setAuswahl(new Set());
    setErgebnisse({});
    try {
      const liste = await nachversandService.ladeFaelle();
      setFaelle(liste);
      setPhase('bereit');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Offene Fälle konnten nicht geladen werden.');
      setPhase('fehler');
    }
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  // Kunden für den Filter, alphabetisch, mit Anzahl offener Fälle
  const kunden = useMemo(() => {
    const map = new Map<string, { id: string; label: string; anzahl: number }>();
    for (const f of faelle) {
      const k = map.get(f.customer_id);
      if (k) {
        k.anzahl++;
      } else {
        map.set(f.customer_id, { id: f.customer_id, label: `${f.company} (${f.mandantennummer})`, anzahl: 1 });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [faelle]);

  // Sichtbare Zeilen: nach Kunde, Mitarbeiter und Monat sortiert
  const sichtbar = useMemo(() => {
    const liste = kundeFilter === 'alle' ? faelle : faelle.filter(f => f.customer_id === kundeFilter);
    return [...liste].sort((a, b) =>
      a.company.localeCompare(b.company, 'de')
      || a.name.localeCompare(b.name, 'de')
      || a.personalnummer - b.personalnummer
      || a.jahr - b.jahr
      || a.monat - b.monat
    );
  }, [faelle, kundeFilter]);

  const waehlbar = (f: NachversandFall) => f.versendbar && !ergebnisse[f.zustellung_id];

  const umschalten = (id: string) => {
    setAuswahl(prev => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };

  const alleAuswaehlen = () => {
    setAuswahl(new Set(sichtbar.filter(waehlbar).map(f => f.zustellung_id)));
  };

  const auswahlAufheben = () => setAuswahl(new Set());

  const handleSenden = async () => {
    const ausgewaehlt = faelle.filter(f => auswahl.has(f.zustellung_id) && waehlbar(f));
    if (ausgewaehlt.length === 0) return;

    // Gruppen je Kunde und Monat, aufgeteilt in Aufrufe zu höchstens MAX_JE_AUFRUF
    const gruppen = new Map<string, NachversandFall[]>();
    for (const f of ausgewaehlt) {
      const schluessel = `${f.customer_id}|${f.jahr}|${f.monat}`;
      if (!gruppen.has(schluessel)) gruppen.set(schluessel, []);
      gruppen.get(schluessel)!.push(f);
    }
    const aufrufe: NachversandFall[][] = [];
    for (const liste of Array.from(gruppen.values())) {
      for (let i = 0; i < liste.length; i += MAX_JE_AUFRUF) {
        aufrufe.push(liste.slice(i, i + MAX_JE_AUFRUF));
      }
    }

    setPhase('sendet');
    setFehler(null);
    setFortschritt({ erledigt: 0, gesamt: ausgewaehlt.length });

    let erledigt = 0;
    let etwasVersendet = false;
    for (const aufruf of aufrufe) {
      const erste = aufruf[0];
      const idByPnr = new Map<number, string>(aufruf.map(f => [f.personalnummer, f.zustellung_id]));
      const neu: Record<string, ZeilenErgebnis> = {};
      try {
        const res = await nachversandService.sende({
          customer_id: erste.customer_id,
          jahr: erste.jahr,
          monat: erste.monat,
          personalnummern: aufruf.map(f => f.personalnummer)
        });
        for (const v of res.versendet) {
          const id = idByPnr.get(v.personalnummer);
          if (id) neu[id] = { art: 'versendet' };
        }
        for (const n of res.nicht_moeglich) {
          const id = idByPnr.get(n.personalnummer);
          if (id) neu[id] = { art: 'nicht_moeglich', text: n.grund };
        }
        for (const e of res.fehler) {
          const id = idByPnr.get(e.personalnummer);
          if (id) neu[id] = { art: 'fehler', text: e.grund };
        }
        for (const p of res.ohne_dokument ?? []) {
          const id = idByPnr.get(p);
          if (id) neu[id] = { art: 'uebersprungen', text: 'Kein Dokument mehr im Spiegel oder bereits direkt zugestellt' };
        }
        for (const p of res.nicht_mehr_offen) {
          const id = idByPnr.get(p);
          if (id) neu[id] = { art: 'uebersprungen', text: 'Nicht mehr offen' };
        }
        // Jede Zeile des Aufrufs bekommt ein Ergebnis, auch wenn die Antwort
        // sie nicht nennt. Lieber sichtbar unklar als stillschweigend offen.
        for (const f of aufruf) {
          if (!neu[f.zustellung_id]) {
            neu[f.zustellung_id] = { art: 'fehler', text: 'Keine Rückmeldung zu diesem Mitarbeiter' };
          }
        }
        if (res.versendet.length > 0) etwasVersendet = true;
      } catch (err) {
        const text = err instanceof Error ? err.message : 'Nachversand fehlgeschlagen';
        for (const f of aufruf) neu[f.zustellung_id] = { art: 'fehler', text };
      }
      erledigt += aufruf.length;
      setErgebnisse(prev => ({ ...prev, ...neu }));
      setFortschritt({ erledigt, gesamt: ausgewaehlt.length });
    }

    setAuswahl(new Set());
    setPhase('fertig');
    if (etwasVersendet) onSuccess();
  };

  const zaehle = (art: ZeilenErgebnis['art']) => Object.values(ergebnisse).filter(e => e.art === art).length;
  const anzahlAuswahl = faelle.filter(f => auswahl.has(f.zustellung_id) && waehlbar(f)).length;
  const sendetGerade = phase === 'sendet';

  const zelle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '13px', color: '#111827', verticalAlign: 'top' };
  const kopf: React.CSSProperties = { ...zelle, fontWeight: 600, color: '#6B7280', fontSize: '12px', backgroundColor: '#F9FAFB', position: 'sticky', top: 0 };

  const kleinerButton: React.CSSProperties = {
    padding: '8px 12px',
    backgroundColor: 'white',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };

  const statusZelle = (f: NachversandFall) => {
    const e = ergebnisse[f.zustellung_id];
    if (e?.art === 'versendet') {
      return <span style={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle2 size={14} /> Versendet</span>;
    }
    if (e?.art === 'fehler') {
      return <span style={{ color: '#DC2626' }}>Fehler: {e.text}</span>;
    }
    if (e?.art === 'nicht_moeglich') {
      return <span style={{ color: '#B45309' }}>Nicht möglich: {e.text}</span>;
    }
    if (e?.art === 'uebersprungen') {
      return <span style={{ color: '#6B7280' }}>{e.text}</span>;
    }
    if (!f.versendbar) {
      return <span style={{ color: '#B45309' }}>{f.grund ?? 'Derzeit nicht versendbar'}</span>;
    }
    return <span style={{ color: '#6B7280' }}>offen</span>;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '1100px',
          width: '94%',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '8px',
            backgroundColor: '#0D948815', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Send size={18} color="#0D9488" />
          </div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
            Nachversand an Mitarbeiter
          </h2>
        </div>

        {/* Info Box */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#F0FDFA', border: '1px solid #99F6E4', borderRadius: '8px', fontSize: '13px', color: '#115E59' }}>
          Hier stehen Mitarbeiter, deren Gehaltsunterlagen eines Monats nur im Sammelpaket an den Arbeitgeber
          gingen und die inzwischen eine E-Mail-Adresse in DATEV haben. Ausgewählte Monate erhalten sie genauso
          per E-Mail wie beim regulären Lohnschein-Versand, mit dem aktuellen Stand der Unterlagen aus DATEV.
          Die E-Mails gehen innerhalb weniger Minuten über den regulären Versand raus. Wer bereits nachversendet
          wurde, verschwindet aus der Liste.
        </div>

        {/* Steuerleiste */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <select
            value={kundeFilter}
            onChange={(e) => setKundeFilter(e.target.value)}
            disabled={sendetGerade || phase === 'laden'}
            style={{
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              color: '#111827',
              minWidth: '280px'
            }}
          >
            <option value="alle">Alle Kunden ({faelle.length})</option>
            {kunden.map(k => (
              <option key={k.id} value={k.id}>{k.label} ({k.anzahl})</option>
            ))}
          </select>
          <button style={kleinerButton} onClick={alleAuswaehlen} disabled={sendetGerade || phase === 'laden'}>
            Alle auswählen
          </button>
          <button style={kleinerButton} onClick={auswahlAufheben} disabled={sendetGerade || phase === 'laden'}>
            Auswahl aufheben
          </button>
          <button style={kleinerButton} onClick={laden} disabled={sendetGerade || phase === 'laden'}>
            <RefreshCw size={14} /> Liste neu laden
          </button>
          <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#6B7280' }}>
            {sichtbar.length} {sichtbar.length === 1 ? 'Fall' : 'Fälle'} angezeigt, {anzahlAuswahl} ausgewählt
          </div>
        </div>

        {/* Fehler */}
        {fehler && (
          <div style={{
            padding: '12px 14px', marginBottom: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5',
            borderRadius: '8px', color: '#991B1B', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <AlertTriangle size={16} color="#DC2626" /> {fehler}
          </div>
        )}

        {/* Fortschritt / Ergebnis */}
        {(phase === 'sendet' || phase === 'fertig') && (
          <div style={{
            padding: '12px 14px', marginBottom: '12px', borderRadius: '8px', fontSize: '13px',
            backgroundColor: phase === 'sendet' ? '#EFF6FF' : '#ECFDF5',
            border: `1px solid ${phase === 'sendet' ? '#93C5FD' : '#6EE7B7'}`,
            color: phase === 'sendet' ? '#1E40AF' : '#065F46'
          }}>
            {phase === 'sendet' ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Loader2 size={16} /> Wird nachversendet: {fortschritt.erledigt} von {fortschritt.gesamt}
              </span>
            ) : (
              <span>
                Fertig: {zaehle('versendet')} versendet
                {zaehle('nicht_moeglich') > 0 && `, ${zaehle('nicht_moeglich')} nicht möglich`}
                {zaehle('uebersprungen') > 0 && `, ${zaehle('uebersprungen')} übersprungen`}
                {zaehle('fehler') > 0 && `, ${zaehle('fehler')} mit Fehler`}
                . Versendete Fälle verschwinden beim nächsten Laden der Liste.
              </span>
            )}
          </div>
        )}

        {/* Liste */}
        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px', minHeight: '160px' }}>
          {phase === 'laden' ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <Loader2 size={28} color="#9CA3AF" />
              </div>
              Offene Fälle werden geladen...
            </div>
          ) : sichtbar.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
              Keine offenen Fälle. Alle Mitarbeiter mit E-Mail-Adresse wurden direkt beliefert.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...kopf, width: '36px' }}></th>
                  <th style={{ ...kopf, textAlign: 'left' }}>Kunde</th>
                  <th style={{ ...kopf, textAlign: 'left' }}>Mitarbeiter</th>
                  <th style={{ ...kopf, textAlign: 'left' }}>E-Mail</th>
                  <th style={{ ...kopf, textAlign: 'left' }}>Monat</th>
                  <th style={{ ...kopf, textAlign: 'left' }}>Sammelpaket am</th>
                  <th style={{ ...kopf, textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sichtbar.map(f => {
                  const aktiv = waehlbar(f);
                  return (
                    <tr key={f.zustellung_id} style={{ opacity: f.versendbar ? 1 : 0.6 }}>
                      <td style={zelle}>
                        <input
                          type="checkbox"
                          checked={auswahl.has(f.zustellung_id)}
                          disabled={!aktiv || sendetGerade}
                          onChange={() => umschalten(f.zustellung_id)}
                        />
                      </td>
                      <td style={zelle}>
                        {f.company}
                        <div style={{ fontSize: '11px', color: '#6B7280' }}>Mandant {f.mandantennummer}</div>
                      </td>
                      <td style={zelle}>
                        {f.name}
                        <div style={{ fontSize: '11px', color: '#6B7280' }}>PNr {f.personalnummer}</div>
                      </td>
                      <td style={zelle}>{f.email}</td>
                      <td style={zelle}>{f.monat_label}</td>
                      <td style={zelle}>{datumBerlin(f.sammelpaket_am)}</td>
                      <td style={zelle}>{statusZelle(f)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Aktionen */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={handleSenden}
            disabled={anzahlAuswahl === 0 || sendetGerade}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: anzahlAuswahl > 0 && !sendetGerade ? '#0D9488' : '#D1D5DB',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: anzahlAuswahl > 0 && !sendetGerade ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Send size={16} />
            {sendetGerade ? 'Wird nachversendet...' : `Nachversenden (${anzahlAuswahl})`}
          </button>
          <button
            onClick={onClose}
            disabled={sendetGerade}
            style={{
              padding: '12px 24px',
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: sendetGerade ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            {sendetGerade ? 'Bitte warten...' : 'Schließen'}
          </button>
        </div>
      </div>
    </div>
  );
};
